import { SidePanelRouter } from "@/app/router/SidePanelRouter";
import { SplitView } from "@/components/layout";
import { Sidebar } from "@/components/layout/components/sidebar/sidebar";
import { TopBar } from "@/components/layout/components/top-bar";
import { MDSMediaQueries } from "@/mds/foundations";
import { isMac } from "@/domains/platform/isMac";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { useCallback, useLayoutEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { clientEnvModule } from "@/modules/client-env";
import { EventContext } from "@/domains/metrics/context";
import { useAuthenticatedMetrics } from "@/app/outlets/layout/hooks";
import { StandardModeAppAppOverlays } from "@/app/outlets/layout/standard-mode-app-overlays";
import { ImportWizard } from "@/components/layout/components/sidebar/import-wizard";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { CenterPanelSplitView } from "@/components/layout/components/center-panel-split-view/center-panel-split-view";
import { noop } from "lodash-es";
import { matchPath } from "react-router-dom";
import { appRoutes } from "@/app/router";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { DesktopAnalyticsBridge } from "@/components/desktop/desktop-analytics-bridge";
import { ImportModalProvider, useImportModal } from "@/contexts/ImportModalContext";
import { SystemPermissionsManager } from "@/components/desktop/system-permissions";

interface StandardModeAppLayoutOutletProps {}

interface KeyboardShortcut {
  condition: ({ event }: { event: KeyboardEvent }) => boolean;
  key: string;
  action: ({ event }: { event: KeyboardEvent }) => void;
}

const StandardModeAppLayoutOutletInner = observer(() => {
  useAuthenticatedMetrics();
  const { store, pageStore } = useAppStore();
  const { isImportModalOpen, setIsImportModalOpen, closeImportModal } = useImportModal();

  const isDesktop = clientEnvModule.isDesktop();

  const keyboardShortcuts: KeyboardShortcut[] = useMemo(
    () => [
      {
        condition: ({ event }) =>
          !pageStore.quickSearchModal.isOpen &&
          !store.notes.newNoteIsBeingCreated &&
          (isMac() ? (isDesktop ? event.metaKey : event.ctrlKey) : event.altKey),
        key: "n",
        action: () => store.notes.composeNewNote({ eventContext: EventContext.KeyboardShortcut }),
      },
      {
        condition: ({ event }) => (isMac() ? event.metaKey : event.ctrlKey),
        key: "k",
        action: pageStore.quickSearchModal.toggleIsOpen,
      },
      {
        condition: ({ event }) =>
          !pageStore.quickSearchModal.isOpen && (isMac() ? event.metaKey : event.ctrlKey),
        key: "j",
        action: () =>
          store.sidePanel.goToMemIt(undefined, {
            openChatPanel: true,
          }),
      },
      {
        condition: ({ event }) =>
          !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey,
        key: "Escape",
        action: () => {
          const { activeTabId: tabId } = store.navigation.tabs;
          const tab = store.navigation.tabs.tabsMap.get(tabId);
          const noteIdMatch = matchPath(
            appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
            tab?.path ?? ""
          );
          if (noteIdMatch) {
            // If find in note is open, close it and return
            const notesViewPageStore = pageStore.getPageStore(
              tab ?? null,
              tab?.path ?? "",
              "notesViewPage"
            ) as NotesViewPageStore | null;
            if (notesViewPageStore?.findInNote?.isOpen) {
              notesViewPageStore.findInNote.close();
              return;
            }
            store.navigation.tabs.closeTab({ tabId });
          }
          store.navigation.tabs.openInNewTab({ path: "/notes" });
        },
      },
      {
        condition: ({ event }) =>
          isMac() ? (isDesktop ? event.metaKey : event.ctrlKey) : event.altKey,
        key: "w",
        action: () => {
          const activeTab = store.navigation.tabs.tabsMap.get(store.navigation.tabs.activeTabId);
          if (!activeTab?.isDefault) {
            store.navigation.tabs.closeTab({ tabId: store.navigation.tabs.activeTabId });
          }
        },
      },
      {
        condition: ({ event }) =>
          isMac() ? (isDesktop ? event.metaKey : event.ctrlKey) : event.altKey,
        key: "t",
        action: pageStore.quickSearchModal.toggleIsOpen,
      },
      {
        condition: ({ event }) => {
          if (!(isMac() ? event.metaKey : event.ctrlKey)) return false;

          // Check if current tab is a notes view
          const { activeTabId: tabId } = store.navigation.tabs;
          const tab = store.navigation.tabs.tabsMap.get(tabId);
          const noteIdMatch = matchPath(
            appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
            tab?.path ?? ""
          );
          return !!noteIdMatch;
        },
        key: "f",
        action: () => {
          const { activeTabId: tabId } = store.navigation.tabs;
          const tab = store.navigation.tabs.tabsMap.get(tabId);
          const notesViewPageStore = pageStore.getPageStore(
            tab ?? null,
            tab?.path ?? "",
            "notesViewPage"
          ) as NotesViewPageStore | null;
          notesViewPageStore?.findInNote?.open();
        },
      },
      {
        condition: ({ event }) => {
          if (!(isMac() ? event.metaKey : event.ctrlKey)) return false;

          // Check if current tab is a notes view and findInNote is open
          const { activeTabId: tabId } = store.navigation.tabs;
          const tab = store.navigation.tabs.tabsMap.get(tabId);
          const noteIdMatch = matchPath(
            appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
            tab?.path ?? ""
          );
          if (!noteIdMatch) return false;

          const notesViewPageStore = pageStore.getPageStore(
            tab ?? null,
            tab?.path ?? "",
            "notesViewPage"
          ) as NotesViewPageStore | null;
          return notesViewPageStore?.findInNote?.isOpen ?? false;
        },
        key: "g",
        action: () => {
          const { activeTabId: tabId } = store.navigation.tabs;
          const tab = store.navigation.tabs.tabsMap.get(tabId);
          const notesViewPageStore = pageStore.getPageStore(
            tab ?? null,
            tab?.path ?? "",
            "notesViewPage"
          ) as NotesViewPageStore | null;
          notesViewPageStore?.findInNote?.findNext();
        },
      },
    ],
    [pageStore, store, isDesktop]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      for (const shortcut of keyboardShortcuts) {
        if (event.key === shortcut.key && shortcut.condition({ event })) {
          event.preventDefault();
          event.stopPropagation();

          shortcut.action({ event });

          return;
        }
      }
    },
    [keyboardShortcuts]
  );

  useLayoutEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const currentSidebarVariant = store.layout.currentSidebarVariant;
  const shouldHideSidebarToggle = store.layout.shouldHideSidebarToggle;
  const toggleSidebar = store.interface.toggleSidebar;

  useEffectOnMount(() => {
    // The sidebar state starts as closed. Determine if it should be opened on load.
    if (store.interface.isSidebarOpen) return;
    if (store.layout.hasRoomForSidebarOnInitialLoad) {
      store.interface.setIsSidebarOpen(true, { animate: false });
    }
  });

  return (
    <>
      <ImportWizard isOpen={isImportModalOpen} onClose={closeImportModal} onSkip={noop} />
      <StandardModeAppAppOverlays />
      <SystemPermissionsManager />
      <DesktopAnalyticsBridge />
      <OuterWrapper data-component="OuterWrapper">
        <Sidebar
          currentSidebarVariant={currentSidebarVariant}
          setIsImportModalOpen={setIsImportModalOpen}
          shouldHideSidebarToggle={shouldHideSidebarToggle}
          toggleSidebar={toggleSidebar}
          data-component="Sidebar"
        />
        <InnerWrapper data-component="InnerWrapper">
          <TopBar
            setIsImportModalOpen={setIsImportModalOpen}
            currentSidebarVariant={currentSidebarVariant}
            data-component="TopBar"
          />
          <Main data-component="Main">
            <SplitView sidePanel={<SidePanelRouter />}>
              <CenterPanel data-component="CenterPanel">
                <CenterPanelSplitView data-component="CenterPanelSplitView" />
              </CenterPanel>
            </SplitView>
          </Main>
        </InnerWrapper>
      </OuterWrapper>
    </>
  );
});

export const StandardModeAppLayoutOutlet = observer<StandardModeAppLayoutOutletProps>(() => {
  return (
    <ImportModalProvider>
      <StandardModeAppLayoutOutletInner />
    </ImportModalProvider>
  );
});

const OuterWrapper = styled.div`
  height: 100%;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.colors.surface.chrome};
  min-height: 0;
`;
OuterWrapper.displayName = "OuterWrapper";

const InnerWrapper = styled.div({
  height: "100%",
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flexShrink: 1,
});
InnerWrapper.displayName = "InnerWrapper";

const Main = styled("div")({
  height: "100%",
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  flexShrink: 1,
  minHeight: 0,
  [MDSMediaQueries.rules.tablet]: {
    paddingLeft: 0,
  },
  [MDSMediaQueries.rules.desktop]: {
    paddingLeft: 0,
  },
});
Main.displayName = "Main";

const CenterPanel = styled.div({
  width: "100%",
  height: "100%",
});
CenterPanel.displayName = "CenterPanel";
